import React, { useEffect, useMemo, useRef } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Input from "./Input";
import { observer } from "mobx-react-lite";
import { AngleDoubleDown } from "styled-icons/fa-solid/AngleDoubleDown";
import styled from "styled-components";
import {
  objectIdentity,
  requestClose,
  requestOpen,
} from "./date_picker_manager";
import { closeAndRefocusPicker, registerManagedDatePicker } from "./managed_date_picker";

const CopyDownArrow = styled(AngleDoubleDown)`
  margin-left: 5px;
  margin-right: 5px;
  cursor: pointer;
`;

function RequestedTestDate({
  sample,
  placeholder = "",
  mandatory = true,
  fullWidth = true,
  highlighted = false,
  onFocus,
  forceUpdate,
  copyDown,
  gridCol,
}) {
  const pickerRef = useRef(null);
  const skipNextOpenOnFocusRef = useRef(false);

  const pickerKey = useMemo(() => {
    // sample is a MobX observable; use WeakMap identity without mutating the object.
    return `requested_test_date::${objectIdentity(sample)}`;
  }, [sample]);

  useEffect(() => {
    return registerManagedDatePicker({ key: pickerKey, pickerRef });
  }, [pickerKey]);

  const _handleKeyDown = (e) => {
    if (e.ctrlKey && e.keyCode === 68) {
      copyDown && copyDown();
      e.preventDefault();
    }
  };

  const updateValueFromDate = (date) => {
    if (
      date &&
      moment(date).isValid() &&
      (moment(date) >= moment().add(2, "years") || moment(date) <= moment().add(-200, "days"))
    ) {
      sample.requested_test_date = "";
      forceUpdate();
      return;
    }

    sample.requested_test_date = date ? moment(date).format("DD/MM/YYYY") : null;
    forceUpdate();

    // Keep focus after selecting a date so keyboard navigation continues.
    // Avoid re-opening loop by skipping the next onFocus-triggered open.
    closeAndRefocusPicker({
      key: pickerKey,
      pickerRef,
      setSkipNextOpen: (v) => (skipNextOpenOnFocusRef.current = v),
    });
  };

  const clear = () => {
    sample.requested_test_date = null;
    forceUpdate();
  };

  const valid = () => {
    return mandatory && sample.requested_test_date;
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        alignItems: "center",
        backgroundColor: valid() ? "" : "#FFBBBB",
      }}
    >
      <DatePicker
        ref={pickerRef}
        customInput={
          <Input
            placeholder={placeholder}
            required={mandatory}
            fullWidth={false}
            highlighted={highlighted}
            data-sample-grid-col={gridCol}
            data-sample-grid-datepicker="true"
            data-date-picker-key={pickerKey}
          />
        }
        value={sample.requested_test_date}
        onChange={updateValueFromDate}
        dateFormat="dd/MM/yyyy"
        preventOpenOnFocus={true}
        onFocus={(e) => {
          onFocus && onFocus(e);
          if (skipNextOpenOnFocusRef.current) {
            skipNextOpenOnFocusRef.current = false;
            return;
          }
          requestOpen(pickerKey);
        }}
        onInputClick={() => requestOpen(pickerKey)}
        onBlur={() => requestClose(pickerKey)}
        onKeyDown={_handleKeyDown}
        isClearable={true}
        onClickOutside={() => requestClose(pickerKey)}
        onChangeRaw={(e) => {
          if (!e.target.value) clear();
        }}
      />
      <CopyDownArrow size={15} onClick={copyDown} />
    </div>
  );
}

export default observer(RequestedTestDate);
